import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getJobById } from "../../endpoints/job.service";
import { formatDate } from "../../utils/helperFunction";
import { useSelector } from "react-redux";
import { JobType, skillsUsed } from "../../utils/constants";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import { ChevronRight } from "lucide-react";
import { getClassificationSummary } from "../../utils/classification";

const JobDetailPage = ({ jobId, selectedJob }) => {
  const navigate = useNavigate();
  const { logged_in } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [jobDetails, setJobDetails] = useState();
  const [activeTab, setActiveTab] = useState("description");
  const job = JobType.find((x) => x.value === jobDetails?.jobType)?.label;

  useEffect(() => {
    if (jobId && logged_in) {
      setLoading(true);
      getJobById({jobId})
        .then((response) => {
          const { success, data } = response;
          if (success) {
            setJobDetails(data);
          }
        })
        .finally(() => setLoading(false));
    } else if (jobId) {
      navigate(`/job-detail/${jobId}`);
    }
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex justify-center mt-64">
        <LoadingAnimation color={"black"} />
      </div>
    );
  }

  return (
    <div className="flex rounded-lg w-full p-6">
      <div className="flex flex-1 flex-col max-h-fit overflow-y-scroll no-scrollbar gap-3 w-full">
        <div className="flex items-center gap-2">
          <p className="flex flex-1 font-medium text-xs text-light-black">
            Posted {formatDate(jobDetails?.updatedAt)}
          </p>
          <div className="flex gap-2">
            <button
              className="flex justify-center font-medium text-xs text-white rounded-lg bg-[#2196F3] px-5 py-2 items-center gap-2"
              onClick={() => navigate(`${jobId}`)}
              disabled={selectedJob?.applied === true}
              role="button"
            >
              {selectedJob?.applied === true ? "Applied" : "Apply"}
            </button>
            <button className="flex justify-center font-medium text-xs text-charcoal rounded-lg px-5 py-2 items-center gap-2 bg-[#F1F5F9] border border-[#ECECEC]">
              Refer
            </button>
            <button className="flex justify-center font-medium text-xs text-charcoal rounded-lg px-5 py-2 items-center gap-2 bg-[#F1F5F9] border border-[#ECECEC]">
              Track
            </button>
          </div>
        </div>
        <button
          className="flex justify-center font-medium text-xs self-end underline text-[#2196F3] items-center gap-1"
          onClick={() => navigate(`${jobId}`)}
        >
          See application <ChevronRight width={16} />
        </button>
        {/* Left section */}
        <div className="flex flex-col flex-1 mt-3 gap-6">
          <div className="flex justify-between items-center">
            <p className="font-medium text-charcoal text-xl">
              {jobDetails?.title}
            </p>
            <span className="font-normal text-light-black text-xs">
              {jobDetails?.refNo}
            </span>
          </div>

          <div className="flex flex-col gap-[14px]">
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">Agency:</span>
              <span className="font-medium text-charcoal text-xs flex-1">
                {selectedJob?.companyDetails?.name ?? "N/A"}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">Location:</span>
              <span className="font-medium text-charcoal text-xs flex-1">
                {jobDetails?.location}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">
                Industry Type:
              </span>
              <span className="font-medium text-charcoal text-xs flex-1">
                {jobDetails?.industryType}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">
                Classification:
              </span>
              <span className="font-medium text-charcoal text-xs flex-1">
                {getClassificationSummary(jobDetails?.classification)}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">Job Type:</span>
              <span className="font-medium text-charcoal text-xs flex-1">
                {job}
              </span>
            </div>
            <div className="flex gap-5">
              <span className="text-xs text-light-black w-28">
                Salary Range:
              </span>
              <span className="font-medium text-charcoal text-xs flex-1">
                £{jobDetails?.salary.min} - £{jobDetails?.salary.max}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-10">
          {/* Tab buttons */}
          <div className="flex rounded space-x-12 mb-6 py-2 px-5 bg-[#F1F5F9]">
            <button
              onClick={() => setActiveTab("description")}
              className={`py-1 text-xs font-semibold  border-b ${
                activeTab === "description"
                  ? "border-primary text-charcoal"
                  : "text-[#6F6F6F]  border-transparent"
              }`}
            >
              Job Description
            </button>
            <button
              onClick={() => setActiveTab("skills")}
              className={`py-1 text-xs font-semibold  border-b ${
                activeTab === "skills"
                  ? "border-primary text-charcoal"
                  : "text-[#6F6F6F]  border-transparent"
              }`}
            >
              Skills Required
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "description" && (
            <div className="my-3">
              <p
                dangerouslySetInnerHTML={{
                  __html: jobDetails?.description,
                }}
                className="text-light-black text-xs mt-2"
              />
            </div>
          )}

          {activeTab === "skills" && jobDetails?.skills?.length > 0 && (
            <div className="w-3/4">
              <div className="grid flex-1 mt-7 mb-2 justify-center grid-cols-[1.9fr_0.75fr_0.6fr_1fr]">
                {["Required skill", "Rating", "Years", "When"].map((label) => (
                  <span
                    key={label}
                    className="text-xs font-semibold text-gray-700 flex-1"
                  >
                    {label}
                  </span>
                ))}
              </div>
              <div className="overflow-auto h-full">
                {jobDetails?.skills?.map((skill) => {
                  const lastUsed = Object.entries(skillsUsed).find(
                    ([key, value]) => value === skill.lastUsed
                  );
                  return (
                    <div
                      key={skill?._id}
                      className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]"
                    >
                      {[
                        skill.title,
                        `${skill.rating}/10`,
                        skill.years,
                        lastUsed ? lastUsed[0] : "N/A",
                      ].map((value, index) => (
                        <div
                          key={index}
                          className="border border-[#FAFAFA] bg-white px-4 py-2.5 text-xs font-medium text-charcoal rounded flex flex-1"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;
